import { useEffect,useState } from "react";
import { useSearchParams } from "react-router-dom";
import RightTimeApi from "@/API";
import { collectClientEnvironment } from "@/utils/clientEnvironment";
import {
    clearTakerTestIdentity,
    getStoredTakerCttId,
    getStoredTakerTestCode,
    persistTakerTestIdentity,
    shouldSendStoredCodeForCtt,
} from "@/utils/takerTestStorage";
import { apiMessage } from "@/utils/apiMessage";
import { readQStartQuery } from "@/utils/qstartParams";

export default function QStart(props: any) {
    const [searchParams] = useSearchParams(); // Hook to access query parameters
    const [message, setMessage] = useState("One moment, preparing questionnaire...");

    useEffect(() => {
        createTest();
    }, []);

    async function createTest() {
        const query = readQStartQuery(searchParams);
        const cttId = query.cttId;
        const firstName = query.firstName;
        const lastName = query.lastName;
        const location = query.location;
        const job = query.job;
        const company = query.company;
        const companyId = query.companyId;

        const parsedCttId = Number(cttId);

        const data: any = {};
        if (cttId && Number.isFinite(parsedCttId) && parsedCttId > 0) data.cttId = parsedCttId;
        if (firstName) data.firstName = firstName;
        if (lastName) data.lastName = lastName;
        if (location) data.location = location;
        if (job) data.job = job;
        if (company) data.company = company;
        if (!company && companyId) data.company = companyId;
        data.clientEnvironment = collectClientEnvironment();

        // Audit logging must not block ThirdPartyTest — a hung PUT /log leaves the taker
        // on a blank "preparing" page and Playwright waits for StartTest forever.
        void RightTimeApi.putAsync("/log", {
            companyId: data.companyId || Number(companyId) || 0,
            context: "QStart request",
            userAgent: navigator.userAgent,
            language: navigator.language,
            referrer: document.referrer,
            message: window.location.href,
            userId: null,
            cttId: data.cttId,
            entityTypeId: 1, // ENTITY_TYPE.Test
            actionId: 1, // ACTION_TYPE.Create
        }).catch((error) => {
            console.error("Failed to log QStart request:", error);
        });

        const startOver = query.startOver;
        if (startOver) {
            clearTakerTestIdentity();
        } else {
            const storedCode = getStoredTakerTestCode();
            const storedCttId = getStoredTakerCttId();
            const requestedCttId = Number(data.cttId || 0);
            if (storedCttId > 0 && requestedCttId > 0 && storedCttId !== requestedCttId) {
                clearTakerTestIdentity();
            } else if (shouldSendStoredCodeForCtt(storedCode, storedCttId, data.cttId)) {
                data.code = storedCode;
            }
        }

        let resp;
        try {
            resp = await RightTimeApi.postAsync("/ThirdPartyTest/", data);
        } catch (error) {
            console.error("ThirdPartyTest request failed:", error);
            setMessage("Unable to prepare questionnaire. Please verify your link and try again.");
            return;
        }
        if (resp && resp.ok) {
            let baseUrl = import.meta.env.VITE_TEST_RUNNER_URL;
            if (resp.jsonData && resp.jsonData.code && baseUrl) {
                const cttEntryCode = String(resp.jsonData.cttCode || "").trim();
                persistTakerTestIdentity(
                    resp.jsonData.code,
                    data.cttId,
                    cttEntryCode || resp.jsonData.code
                );
                baseUrl += resp.jsonData.code;
                window.location.href = baseUrl;
            }
        } else {
            let errorMessage = "Unable to prepare questionnaire. Please verify your link and try again.";

            const fromApi = apiMessage(resp);
            if (fromApi)
                errorMessage = fromApi;
            else if (resp) {
                try {
                    const textMessage = await resp.text();
                    if (textMessage && textMessage.trim().length > 0)
                        errorMessage = textMessage;
                } catch {
                    // Keep default message if body text cannot be read.
                }
            }

            setMessage(errorMessage);
        }
    }

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                backgroundColor: "#f9f9f9",
            }}
        >
            <div
                style={{
                    padding: "20px 40px",
                    backgroundColor: "#fff",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontFamily: "Arial, sans-serif",
                    color: "#333",
                }}
            >
                <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}>
                    {message}
                </h1>
            </div>
        </div>
    );
}
